import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Download, FileSpreadsheet, Loader2, UploadCloud } from 'lucide-react';
import { CDMO_IMPORT_MESSAGES } from '../../constants/messages';
import { CDMO_IMPORT_TEMPLATE_PATH } from '../../lib/cdmoImport/columns';
import { downloadAiTemplate } from './helpers';

export interface FileRejection {
  fileName?: string;
  /** `template`: the file does not follow the template, so the panel offers it for download. */
  kind?: 'template' | 'format' | 'empty' | 'limit' | 'file';
  error: string;
  details: string[];
}

interface UploadStepProps {
  busy: boolean;
  rejection: FileRejection | null;
  onFile: (file: File) => void;
}

const UploadStep: React.FC<UploadStepProps> = ({ busy, rejection, onFile }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preparingAiTemplate, setPreparingAiTemplate] = useState(false);

  const getAiTemplate = async () => {
    setPreparingAiTemplate(true);
    try {
      await downloadAiTemplate();
    } catch (error) {
      console.error('AI-ready template could not be built:', error);
      toast.error(CDMO_IMPORT_MESSAGES.AI_TEMPLATE_FAILED);
    } finally {
      setPreparingAiTemplate(false);
    }
  };

  const pick = (files: FileList | null) => {
    const file = files?.[0];
    if (file && !busy) onFile(file);
  };

  return (
    <div className="space-y-5">
      <div
        role="button"
        tabIndex={0}
        aria-label={`${CDMO_IMPORT_MESSAGES.DROP_TITLE}, or press Enter to ${CDMO_IMPORT_MESSAGES.BROWSE.toLowerCase()}`}
        aria-busy={busy}
        data-testid="cdmo-import-dropzone"
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !busy) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pick(e.dataTransfer.files);
        }}
        className={`relative flex flex-col items-center justify-center text-center rounded-2xl border-2 border-dashed px-6 py-12 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-electric-blue focus-visible:ring-offset-2 ${
          busy
            ? 'border-electric-blue/40 bg-electric-blue/5 cursor-wait'
            : dragging
              ? 'border-electric-blue bg-electric-blue/10 cursor-copy'
              : 'border-slate-gray/30 bg-light-gray/50 hover:border-electric-blue/60 hover:bg-electric-blue/5 cursor-pointer'
        }`}
      >
        {busy ? (
          <>
            <Loader2 className="w-10 h-10 text-electric-blue animate-spin mb-4" aria-hidden />
            <p className="text-base font-semibold text-charcoal">{CDMO_IMPORT_MESSAGES.READING_FILE}</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-2xl bg-white shadow-sm border border-electric-blue/20 flex items-center justify-center mb-4">
              <UploadCloud className="w-7 h-7 text-electric-blue" aria-hidden />
            </div>
            <p className="text-base font-semibold text-charcoal">{CDMO_IMPORT_MESSAGES.DROP_TITLE}</p>
            <p className="mt-1 text-sm text-slate-gray">
              or <span className="font-semibold text-selected-blue underline underline-offset-2">{CDMO_IMPORT_MESSAGES.BROWSE.toLowerCase()}</span>
            </p>
            <p className="mt-3 text-xs text-slate-gray">{CDMO_IMPORT_MESSAGES.DROP_HINT}</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          tabIndex={-1}
          data-testid="cdmo-import-file-input"
          onChange={(e) => {
            pick(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {rejection && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-red-800 break-words">
                {rejection.fileName ? `"${rejection.fileName}" was not accepted` : 'File not accepted'}
                {rejection.kind === 'template' && ' — it does not follow the import template'}
              </p>
              <p className="mt-1 text-sm text-red-700">{rejection.error}</p>
              {rejection.details.length > 0 && (
                <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-red-700 break-words">
                  {rejection.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-sm text-red-700">
                No rows were checked and nothing was saved. Fix the file and upload it again
                {rejection.kind === 'template' ? (
                  <>
                    {', or '}
                    <a
                      href={CDMO_IMPORT_TEMPLATE_PATH}
                      download="PCN_CDMO_Import.csv"
                      className="font-semibold text-red-800 underline underline-offset-2 hover:text-red-900"
                    >
                      download the template
                    </a>
                    {' and copy your rows into it.'}
                  </>
                ) : (
                  '.'
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* The AI-ready template replaces the plain CSV download (Phase 6 SOW). The CSV template stays
          available from the wrong-header error above, where it is needed. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-electric-blue/20 bg-electric-blue/5 px-4 py-3 text-sm text-slate-gray">
        <p className="flex items-start gap-2">
          <FileSpreadsheet className="w-4 h-4 text-electric-blue shrink-0 mt-0.5" aria-hidden />
          <span>
            <span className="block font-semibold text-charcoal">{CDMO_IMPORT_MESSAGES.AI_TEMPLATE_TITLE}</span>
            <span id="cdmo-import-ai-template-hint">{CDMO_IMPORT_MESSAGES.AI_TEMPLATE_HINT}</span>
          </span>
        </p>
        <button
          type="button"
          onClick={getAiTemplate}
          disabled={preparingAiTemplate}
          aria-describedby="cdmo-import-ai-template-hint"
          data-testid="cdmo-import-ai-template"
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-electric-blue/30 bg-white px-3 py-2 font-semibold text-selected-blue hover:bg-electric-blue/5 disabled:opacity-60 transition-colors"
        >
          {preparingAiTemplate ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Download className="w-4 h-4" aria-hidden />}
          {preparingAiTemplate ? CDMO_IMPORT_MESSAGES.AI_TEMPLATE_PREPARING : CDMO_IMPORT_MESSAGES.AI_TEMPLATE_LINK}
        </button>
      </div>
    </div>
  );
};

export default UploadStep;
